import { useState } from 'react';
import { CheckCircle2, ShieldCheck } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Checkbox } from '../ui/checkbox';

const digits = (s) => (s || '').replace(/\D/g, '');
export const didProblem = (v) => {
  const d = digits(v);
  if (!d) return null;
  if (d.length !== 15) return `A Director ID has 15 digits (${d.length} entered)`;
  if (!d.startsWith('036')) return 'A Director ID starts with 036';
  return '';
};

const Done = ({ outcome }) => (
  <div className="rounded-lg border border-border bg-card p-6 text-center space-y-2" data-testid="did-submitted">
    <CheckCircle2 className="w-8 h-8 text-primary mx-auto" />
    <p className="font-medium">Thank you – we've received your response.</p>
    <p className="text-sm text-muted-foreground">
      {outcome === 'pending_not_obtained' ? 'Your accountant will follow up with you about applying for a Director ID.' : 'Your accountant will review it shortly. Nothing else is needed from you.'}
    </p>
  </div>
);

export const DirectorIdForm = ({ view, onSubmit }) => {
  const [val, setVal] = useState('');
  const [ok, setOk] = useState(false);
  const [mode, setMode] = useState('enter');
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);
  const [err, setErr] = useState('');
  const problem = didProblem(val);
  const last4 = view.existing_masked ? view.existing_masked.slice(-4) : null;
  if (done) return <Done outcome={done} />;
  const canSend = ok && !busy && (mode !== 'enter' || problem === '');
  const submit = async () => {
    setBusy(true); setErr('');
    try {
      const r = await onSubmit({ director_id: mode === 'enter' ? val : null, not_obtained: mode === 'none', confirm_existing: mode === 'confirm', confirmed: ok });
      setDone(r.outcome);
    } catch (e) { setErr(e.response?.data?.detail || 'Something went wrong – please try again.'); } finally { setBusy(false); }
  };
  return (
    <div className="space-y-5" data-testid="did-form">
      <div>
        <p className="text-xs uppercase tracking-wide text-muted-foreground">Director</p>
        <p className="text-lg font-semibold" data-testid="did-director-name">{view.director}</p>
      </div>
      <div>
        <p className="text-xs uppercase tracking-wide text-muted-foreground mb-1">Your companies with our practice</p>
        <ul className="text-sm space-y-0.5" data-testid="did-companies">
          {view.companies.map((c) => <li key={c.name}>{c.name}{c.acn ? <span className="text-muted-foreground"> · ACN {c.acn}</span> : null}</li>)}
        </ul>
      </div>
      <div className="space-y-2" role="radiogroup" aria-label="Your response">
        {last4 && <label className="flex items-center gap-2 text-sm"><input type="radio" checked={mode === 'confirm'} onChange={() => setMode('confirm')} data-testid="did-mode-confirm" />Confirm my existing Director ID ending {last4} is correct</label>}
        <label className="flex items-center gap-2 text-sm"><input type="radio" checked={mode === 'enter'} onChange={() => setMode('enter')} data-testid="did-mode-enter" />Enter my Director ID</label>
        <label className="flex items-center gap-2 text-sm"><input type="radio" checked={mode === 'none'} onChange={() => setMode('none')} data-testid="did-mode-none" />I have not obtained a Director ID yet</label>
      </div>
      {mode === 'enter' && (
        <div className="space-y-1">
          <Input inputMode="numeric" autoComplete="off" placeholder="036 12345 67890 12" value={val} onChange={(e) => setVal(e.target.value)} className="font-mono tracking-wider max-w-xs" data-testid="did-input" aria-describedby="did-hint" />
          <p id="did-hint" className={`text-xs ${problem ? 'text-destructive' : 'text-muted-foreground'}`} data-testid="did-format-hint">
            {problem || (problem === '' ? 'Format valid – check digit not verified' : '15 digits starting with 036. Spaces are fine.')}
          </p>
        </div>
      )}
      {mode === 'none' && <p className="text-sm text-muted-foreground">You can apply at <a className="underline" href={view.abrs_link} target="_blank" rel="noreferrer">abrs.gov.au</a>. We'll pause reminders and your accountant will be in touch.</p>}
      <label className="flex items-start gap-2 text-sm"><Checkbox checked={ok} onCheckedChange={(v) => setOk(!!v)} data-testid="did-confirm-checkbox" />I confirm this information is correct and relates to me.</label>
      {err && <p className="text-sm text-destructive" role="alert" data-testid="did-error">{err}</p>}
      <Button onClick={submit} disabled={!canSend} data-testid="did-submit-btn">{busy ? 'Sending…' : 'Submit'}</Button>
      <p className="text-xs text-muted-foreground flex items-center gap-1.5"><ShieldCheck className="w-3.5 h-3.5" />{view.validation_note}</p>
    </div>
  );
};
